import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Pagination,
  Chip,
} from "@mui/material";
import { Search as SearchIcon, Close as CloseIcon } from "@mui/icons-material";
import axios from "axios";
import { BASE_URL } from "../../services/configUrls";

const BulkStudentTableSelector = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Local filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [yearFilter, setYearFilter] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // Fetch all students
  const fetchStudents = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.get(
        `${BASE_URL}/institute/student-list/global-cert`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        },
      );

      const data = response.data.total_students_details || [];
      setStudents(data);
    } catch (err) {
      console.error("Error fetching students:", err);
      setError("Failed to load students. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  // Local filtering
  const filteredStudents = students.filter((student) => {
    const nameEmail = `${student.name} ${student.email}`.toLowerCase();
    const searchMatch = nameEmail.includes(searchQuery.toLowerCase());

    const statusMatch =
      statusFilter === "" || student.global_cert_status === statusFilter;

    const yearMatch = yearFilter === "" || student.passoutYear === yearFilter;

    return searchMatch && statusMatch && yearMatch;
  });

  // Update pagination count after filtering
  useEffect(() => {
    setTotalPages(Math.ceil(filteredStudents.length / pageSize));
    setPage(1); // reset page when filter changes
  }, [searchQuery, statusFilter, yearFilter, students]);

  // Current page data
  const paginatedStudents = filteredStudents.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  // Generate dynamic status list
  const statusList = [...new Set(students.map((s) => s.global_cert_status))];

  // Generate dynamic year list
  const yearList = [...new Set(students.map((s) => s.passoutYear))].sort();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Global Certificate Students
        </Typography>

        {/* TOTAL COUNT (FILTERED) */}
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Total Students: {filteredStudents.length}
        </Typography>
      </Box>

      {/* Search + Status + Year Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <TextField
            placeholder="Search by email or name"
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery("")}
                    edge="end"
                  >
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            select
            SelectProps={{ native: true }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            size="small"
            sx={{ width: 220 }}
          >
            <option value="">All Status</option>
            {statusList.map((status, idx) => (
              <option key={idx} value={status}>
                {status}
              </option>
            ))}
          </TextField>

          <TextField
            select
            SelectProps={{ native: true }}
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            size="small"
            sx={{ width: 180 }}
          >
            <option value="">All Years</option>
            {yearList.map((year, idx) => (
              <option key={idx} value={year}>
                {year}
              </option>
            ))}
          </TextField>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Roll No</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Branch</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Year</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedStudents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        {students.length === 0
                          ? "Your institution doesn't have any eligible student for zscaler global certification"
                          : "No students found."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedStudents.map((student) => (
                    <TableRow key={student.id}>
                      <TableCell>{student.name}</TableCell>
                      <TableCell>{student.email}</TableCell>
                      <TableCell>{student.rollNo}</TableCell>
                      <TableCell>{student.branch}</TableCell>
                      <TableCell>{student.passoutYear}</TableCell>
                      <TableCell>
                        <Chip
                          label={student.global_cert_status || "Pending"}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(event, value) => setPage(value)}
              color="primary"
              size="large"
            />
          </Box>
        </>
      )}
    </Container>
  );
};

export default BulkStudentTableSelector;
